import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
    customDateRange,
    DATE_PERIODS,
    type DateBounds,
    type DatePeriod,
    periodDateBounds,
    resolveDatePeriod,
} from '#components/shared/datePeriod.ts';

const parseDate = (value: string | null) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return undefined;
    }
    const date = DateTime.fromISO(value);
    return date.isValid && date.toFormat('yyyy-MM-dd') === value ? date.toJSDate() : undefined;
};

export const useDatePeriodFilter = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawPeriod = searchParams.get('period');
    const parsedPeriod = DATE_PERIODS.find(period => period === rawPeriod) ?? 'all';
    const urlFrom = parseDate(searchParams.get('from'));
    const urlTo = parseDate(searchParams.get('to'));
    const urlCustomRange = parsedPeriod === 'custom' ? customDateRange(urlFrom, urlTo) : undefined;
    const appliedPeriod = parsedPeriod === 'custom' && !urlCustomRange ? 'all' : parsedPeriod;

    const [now, setNow] = useState(Date.now);
    const selectedDates = useMemo(
        () => (appliedPeriod === 'custom' ? { from: urlFrom, to: urlTo } : periodDateBounds(appliedPeriod, now)),
        [appliedPeriod, urlCustomRange?.start, urlCustomRange?.end, now]
    );

    const setPeriod = (period: Exclude<DatePeriod, 'custom'>) => {
        setNow(Date.now());
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            if (period === 'all') {
                next.delete('period');
            } else {
                next.set('period', period);
            }
            next.delete('from');
            next.delete('to');
            return next;
        });
    };

    const setCustomDatesRange = (value?: DateBounds) => {
        const { from: nextFromDate, to: nextToDate } = value ?? {};
        if (!nextFromDate || !nextToDate || !customDateRange(nextFromDate, nextToDate)) {
            return;
        }
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            next.set('period', 'custom');
            next.set('from', DateTime.fromJSDate(nextFromDate).toFormat('yyyy-MM-dd'));
            next.set('to', DateTime.fromJSDate(nextToDate).toFormat('yyyy-MM-dd'));
            return next;
        });
    };

    return {
        period: appliedPeriod,
        setPeriod,
        selectedDates,
        setCustomDatesRange,
        range: appliedPeriod === 'custom' ? urlCustomRange : resolveDatePeriod(appliedPeriod, now),
        hasActiveFilter: appliedPeriod !== 'all',
    };
};

export type DatePeriodFilterState = ReturnType<typeof useDatePeriodFilter>;
